import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { useMediaPredicate } from "react-media-hook";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { formatCurrency } from "@app/utils";

export interface GraphProps {
  data: {
    year: number;
    alpianAmt: number;
    otherAmt: number;
  }[];
}

const Graph: FC<GraphProps> = memo(({ data }) => {
  const { t } = useTranslation("calculator");
  const isMobile = useMediaPredicate("(max-width: 1200px)");
  const padding = isMobile ? 40 : 60;

  return (
    <ResponsiveContainer width="100%" minHeight={500} maxHeight={700}>
      <LineChart data={data} margin={{ top: 10, bottom: 5, left: 0 }}>
        <CartesianGrid
          vertical={false}
          stroke="#333333"
          strokeDasharray="3 3"
        />
        <XAxis
          padding={{ left: padding, right: padding }}
          tickLine={false}
          axisLine={false}
          dataKey="year"
          tick={(props) => (
            <text
              textAnchor="start"
              orientation="right"
              x={props.x}
              y={props.y + 20}
              fontSize={isMobile ? 12 : 14}
              fill="#888888"
            >
              <tspan dy="0.355em">
                {t("x_years", { years: props.payload.value })}
              </tspan>
            </text>
          )}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickCount={19}
          interval={0}
          domain={[(dataMin) => dataMin - dataMin * 0.1, "auto"]}
          tick={(props) => (
            <text
              textAnchor="start"
              orientation="right"
              x={0}
              y={props.y}
              fontSize={isMobile ? 12 : 14}
              fill="#888888"
            >
              <tspan dy="0.355em">{formatCurrency(props.payload.value)}</tspan>
            </text>
          )}
        />
        <Tooltip
          content={(props) => {
            const payload = props.payload[0];
            const alpian = payload?.payload?.alpianAmt;
            const other = payload?.payload?.otherAmt;

            return (
              <div className="text-extra-small">
                <div className="text-green-light">{formatCurrency(alpian)}</div>
                <div className="text-primary-content">
                  {formatCurrency(other)}
                </div>
              </div>
            );
          }}
        />
        <Line
          dot={false}
          type="monotone"
          dataKey="alpianAmt"
          stroke="#008171"
        />
        <Line dot={false} type="monotone" dataKey="otherAmt" stroke="#fff" />
      </LineChart>
    </ResponsiveContainer>
  );
});

Graph.displayName = "Graph";

export { Graph };
